import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { getFeedyardsOptions, supplyChainLotFeedyardsQuery } from "../BenchmarkFilters/FeedyardPicker";
import { lotFormDefaults, lotFormSchema, type LotFormSchema, type LotStatus } from "./validation";
import { getBreedOptions, supplyChainLotBreedsQuery } from "@/benchmarks/BenchmarkFilters/BreedsPicker";
import { type SexClass } from "@/benchmarks/BenchmarkFilters/SexPicker";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { FormDatePicker } from "@/components/FormDatePicker";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { SelectInput } from "@/components/SelectInput";
import { Heading3 } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import { Form, FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { formatContactLocationLabel } from "@/lib/utils/formatting";

export type LotFormProps = {
  onSubmit: (values: LotFormSchema) => Promise<void>;
  onCancel: () => void;
  initialValues?: Partial<LotFormSchema>;
};

const sexClassOptions: Array<{ value: SexClass; label: string }> = [
  { value: "STEER", label: "Steer" },
  { value: "HEIFER", label: "Heifer" },
  { value: "MIXED", label: "Mixed" },
];

const statusOptions: Array<{ value: LotStatus; label: string }> = [
  { value: "OPEN", label: "Open" },
  { value: "CLOSED", label: "Closed" },
];

export function LotForm({ onSubmit, onCancel, initialValues }: LotFormProps) {
  const { activeBusinessUnitId, activeBusinessUnitCountryCode } = useActiveBusinessUnit();

  const form = useForm<LotFormSchema>({
    resolver: zodResolver(lotFormSchema),
    defaultValues: { ...lotFormDefaults, ...initialValues },
  });

  // Query for partners
  const {
    data: partnersData,
    loading: partnersLoading,
    error: partnersError,
  } = useQuery(supplyChainPartnerLocationsQuery, {
    variables: {
      businessUnit: activeBusinessUnitId || "",
      status: ["ACTIVE"],
      sortBy: [{ field: "BUSINESS_NAME", desc: false }],
    },
  });
  const partnerOptions = getLocationOptions({
    edges: partnersData?.supplyChainPartners?.edges || [],
    businessUnitCountryCode: activeBusinessUnitCountryCode,
  });

  // Query for feedyards
  const {
    data: feedyardsData,
    loading: feedyardsLoading,
    error: feedyardsError,
  } = useQuery(supplyChainLotFeedyardsQuery, {
    variables: {
      businessUnit: activeBusinessUnitId || "",
    },
    skip: !activeBusinessUnitId,
  });
  const feedyardsOptions = getFeedyardsOptions(feedyardsData);

  // Query for breeds
  const {
    data: breedsData,
    loading: breedsLoading,
    error: breedsError,
  } = useQuery(supplyChainLotBreedsQuery, {
    variables: { businessUnit: activeBusinessUnitId },
  });

  const breedOptions = getBreedOptions(breedsData);

  const handleSubmit = form.handleSubmit((data) => {
    return onSubmit(data);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-x-3" noValidate>
        {/* Basic Info Section */}
        <div className="col-span-full">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Basic Info</Heading3>
        </div>

        <FormField
          control={form.control}
          name="reference"
          render={({ field }) => (
            <FormItemWithMessage label="Lot ID *" inputComponent={<Input placeholder="Enter lot ID" {...field} />} />
          )}
        />

        <FormField
          control={form.control}
          name="sexClass"
          render={({ field }) => (
            <FormItemWithMessage
              label="Sex Class *"
              inputComponent={
                <SelectInput placeholder="Select sex class" options={sexClassOptions} showClear={false} {...field} />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItemWithMessage
              label="Status *"
              inputComponent={
                <SelectInput placeholder="Select status" options={statusOptions} showClear={false} {...field} />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="sortNote"
          render={({ field }) => (
            <FormItemWithMessage
              label="Sort Note"
              inputComponent={<Input placeholder="Enter sort note" {...field} />}
            />
          )}
        />

        {/* Partners Section */}
        <div className="col-span-full mt-6">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Partners</Heading3>
        </div>

        <FormField
          control={form.control}
          name="feedyardPartner"
          render={({ field }) => (
            <FormItemWithMessage
              label="Feedyard"
              inputComponent={
                <Combobox
                  type="single"
                  loading={feedyardsLoading}
                  selected={field.value || ""}
                  placeholder="Select feedyard"
                  options={feedyardsOptions}
                  isModal
                  error={feedyardsError}
                  {...field}
                />
              }
            />
          )}
        />
        <FormField
          control={form.control}
          name="sourcePartner"
          render={({ field }) => (
            <FormItemWithMessage
              label="Source"
              inputComponent={
                <Combobox
                  type="single"
                  loading={partnersLoading}
                  selected={field.value || ""}
                  placeholder="Select source"
                  options={partnerOptions}
                  isModal
                  error={partnersError}
                  {...field}
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="destinationPartner"
          render={({ field }) => (
            <FormItemWithMessage
              label="Destination"
              inputComponent={
                <Combobox
                  type="single"
                  loading={partnersLoading}
                  selected={field.value || ""}
                  placeholder="Select destination"
                  options={partnerOptions}
                  isModal
                  error={partnersError}
                  {...field}
                />
              }
            />
          )}
        />

        {/* Animal Details Section */}
        <div className="col-span-full mt-6">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Animal Details</Heading3>
        </div>

        <FormField
          control={form.control}
          name="animalBreeds"
          render={({ field }) => (
            <FormItemWithMessage
              label="Breeds"
              inputComponent={
                <Combobox
                  type="multi"
                  loading={breedsLoading}
                  selected={field.value || []}
                  placeholder="Select breeds"
                  options={breedOptions}
                  isModal
                  error={breedsError}
                  {...field}
                />
              }
            />
          )}
        />

        {/* Animal Counts Section */}
        <div className="col-span-full mt-6">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Animal Counts</Heading3>
        </div>

        <FormField
          control={form.control}
          name="numTotalIn"
          render={({ field }) => (
            <FormItemWithMessage
              label="Head In"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalScale={0}
                  maxLength={10}
                  placeholder="Enter count"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="numTotalOut"
          render={({ field }) => (
            <FormItemWithMessage
              label="Head Out"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalScale={0}
                  maxLength={10}
                  placeholder="Enter count"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="numTotalDead"
          render={({ field }) => (
            <FormItemWithMessage
              label="Dead"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalScale={0}
                  maxLength={10}
                  placeholder="Enter count"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="numTotalRemoved"
          render={({ field }) => (
            <FormItemWithMessage
              label="Removed"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalScale={0}
                  maxLength={10}
                  placeholder="Enter count"
                />
              }
            />
          )}
        />

        {/* Weights Section */}
        <div className="col-span-full mt-6">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Weights</Heading3>
        </div>

        <FormField
          control={form.control}
          name="weightTotalIn"
          render={({ field }) => (
            <FormItemWithMessage
              label="Total Weight In"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  decimalScale={0}
                  suffix=" lbs"
                  maxLength={15}
                  placeholder="Enter weight"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="weightTotalOut"
          render={({ field }) => (
            <FormItemWithMessage
              label="Total Weight Out"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  decimalScale={0}
                  suffix=" lbs"
                  maxLength={15}
                  placeholder="Enter weight"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="weightTotalFeedIntake"
          render={({ field }) => (
            <FormItemWithMessage
              label="Total Feed Intake"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  decimalScale={0}
                  suffix=" lbs"
                  maxLength={15}
                  placeholder="Enter weight"
                />
              }
            />
          )}
        />

        {/* Financials Section */}
        <div className="col-span-full mt-6">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Financials</Heading3>
        </div>

        <FormField
          control={form.control}
          name="purchasePrice"
          render={({ field }) => (
            <FormItemWithMessage
              label="Purchase Price"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  prefix="$"
                  decimalScale={2}
                  maxLength={15}
                  placeholder="Enter price"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="salePrice"
          render={({ field }) => (
            <FormItemWithMessage
              label="Sale Price"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  prefix="$"
                  decimalScale={2}
                  maxLength={15}
                  placeholder="Enter price"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="feedCost"
          render={({ field }) => (
            <FormItemWithMessage
              label="Feed Cost"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  prefix="$"
                  decimalScale={2}
                  maxLength={15}
                  placeholder="Enter cost"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="nonFeedCost"
          render={({ field }) => (
            <FormItemWithMessage
              label="Non-Feed Cost"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  prefix="$"
                  decimalScale={2}
                  maxLength={15}
                  placeholder="Enter cost"
                />
              }
            />
          )}
        />

        {/* Quality Grades Section */}
        <div className="col-span-full mt-6">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Quality Grades</Heading3>
        </div>

        <FormField
          control={form.control}
          name="primeFraction"
          render={({ field }) => (
            <FormItemWithMessage
              label="Prime"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  suffix="%"
                  decimalScale={2}
                  maxLength={6}
                  placeholder="Enter percentage"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="choiceFraction"
          render={({ field }) => (
            <FormItemWithMessage
              label="Choice"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  suffix="%"
                  decimalScale={2}
                  maxLength={6}
                  placeholder="Enter percentage"
                />
              }
            />
          )}
        />

        <FormField
          control={form.control}
          name="selectFraction"
          render={({ field }) => (
            <FormItemWithMessage
              label="Select"
              inputComponent={
                <NumericFormat
                  customInput={Input}
                  getInputRef={field.ref}
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.value)}
                  allowNegative={false}
                  thousandSeparator=","
                  decimalSeparator="."
                  suffix="%"
                  decimalScale={2}
                  maxLength={6}
                  placeholder="Enter percentage"
                />
              }
            />
          )}
        />

        {/* Dates Section */}
        <div className="col-span-full mt-6">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Dates</Heading3>
        </div>

        <FormDatePicker<LotFormSchema> name="dateOpened" label="Date Opened" isModal />

        <FormDatePicker<LotFormSchema> name="dateClosed" label="Date Closed" isModal />

        {/* Form Actions */}
        <div className="md:col-span-3 mt-5 flex flex-col md:flex-row md:justify-end gap-3">
          <Button
            disabled={form.formState.isSubmitting}
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="w-full md:w-auto"
          >
            Cancel
          </Button>
          <Button
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            type="submit"
            variant="confirm"
            className="w-full md:w-auto"
          >
            {initialValues ? "Save Changes" : "Save and Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

const getLocationOptions = ({
  edges,
  businessUnitCountryCode,
}: {
  edges: SupplyChainPartnerLocation;
  businessUnitCountryCode?: string | null;
}): ComboboxOption[] => {
  return edges
    .map((edge) => {
      const node = edge?.node;
      const id = node?.id || "";
      const label = formatContactLocationLabel({
        businessName: node?.businessName,
        accountNumber: node?.accountNumber,
        address: node?.defaultLocation?.address,
        businessUnitCountryCode,
      });
      return {
        value: id,
        label: label,
      };
    })
    .filter((option) => option.value !== "");
};

export const supplyChainPartnerLocationsQuery = graphql(`
  query SupplyChainPartnersForLotForm(
    $businessUnit: ID!
    $sortBy: [SupplyChainPartnersColumnSort]
    $status: [SupplyChainPartnerStatusEnumType!]
  ) {
    supplyChainPartners(owners: [$businessUnit], sortBy: $sortBy, status: $status) {
      edges {
        node {
          id
          businessName
          accountNumber
          defaultLocation {
            id
            address {
              id
              line1
              line2
              city
              state
              country {
                id
                isoAlpha2
              }
            }
          }
        }
      }
    }
  }
`);

type SupplyChainPartnerLocation = NonNullable<
  NonNullable<ResultOf<typeof supplyChainPartnerLocationsQuery>>["supplyChainPartners"]
>["edges"];
